/**
 * Card Lookup Utility
 *
 * LOOKUP STRATEGY (in order):
 *   1. Direct ID: {setId}-{localId} from local DB (0ms)
 *   2. Direct ID: {setId}-{localId} from TCGdex (~200ms)
 *   3. Supabase by setId + localId (~100ms)
 *   4. Local DB by name + number (~5ms)
 *   5. TCGdex by name search (~400ms)
 *   6. English fallback for non-English cards
 *   7. Community cards (ai_card_submissions) - User-contributed!
 *
 * MULTI-LANGUAGE SUPPORT:
 *   - TCGdex supports: en, ja, fr, de, it, es, pt
 *   - Local DB is English only (22K+ cards)
 *   - For non-English: Try language-specific TCGdex → Fall back to English
 *
 * COMMUNITY CARDS:
 *   - Cards added via Chase's Research Lab are saved to ai_card_submissions
 *   - These are searched as a last resort for niche/promo cards
 *   - User A adds card → User B can find it when scanning!
 */

import localCardsData from '../data/pokemon_cards.json';
import { supabase } from '../lib/supabase';

// Supported languages by TCGdex
const SUPPORTED_LANGUAGES = ['en', 'ja', 'fr', 'de', 'it', 'es', 'pt'];
const DEFAULT_LANGUAGE = 'en';

// Build lookup maps for O(1) access
let cardsByName = null;
let cardsById = null;
let initialized = false;

/**
 * Normalize card numbers for fuzzy matching
 * Handles leading zeros in both pure numeric and alphanumeric formats:
 *   "020" → "20"
 *   "001" → "1"
 *   "SWSH050" → "SWSH50"
 *   "TG01" → "TG1"
 */
const normalizeCardNumber = (num) => {
  if (!num) return "";

  // Convert to string, trim, uppercase
  let clean = num.toString().trim().toUpperCase();

  // Pure numeric: strip leading zeros by parsing as int
  if (/^\d+$/.test(clean)) {
    return parseInt(clean, 10).toString();
  }

  // Alphanumeric (like SWSH050, TG01): strip leading zeros from numeric suffix
  // "SWSH050" → "SWSH50", "TG01" → "TG1"
  return clean.replace(/([A-Z]+)0+([1-9]\d*)/i, '$1$2');
};

/**
 * Initialize the local card database maps
 * Called automatically on first lookup
 */
function initializeLocalDB() {
  if (initialized) return;

  const startTime = Date.now();
  console.log('[CardLookup] Initializing local database...');

  cardsByName = new Map();
  cardsById = new Map();

  for (const card of localCardsData) {
    // Index by ID for direct lookup (e.g., "ex15-100")
    cardsById.set(card.id, card);

    // Also index by lowercase ID for case-insensitive matching
    cardsById.set(card.id.toLowerCase(), card);

    // Index by name (lowercase) for fallback search
    const nameLower = card.name.toLowerCase();
    if (!cardsByName.has(nameLower)) {
      cardsByName.set(nameLower, []);
    }
    cardsByName.get(nameLower).push(card);
  }

  initialized = true;
  console.log(`[CardLookup] Local DB ready: ${localCardsData.length} cards indexed in ${Date.now() - startTime}ms`);
}

/**
 * DIRECT ID LOOKUP - Primary Strategy
 * Constructs card ID from setId + localId and looks up directly
 * VALIDATES that the card name matches (fuzzy) to catch AI set_id errors
 *
 * @param {string} setId - Set ID from Gemini (e.g., "ex15", "sm9", "swsh4")
 * @param {string} localId - Card number (e.g., "100", "33", "138")
 * @param {string} expectedName - Pokemon name from Gemini for validation
 * @returns {object|null} - Card data or null if not found
 */
function lookupDirectId(setId, localId, expectedName = '') {
  if (!setId || !localId) return null;

  initializeLocalDB();
  const startTime = Date.now();

  // Clean the localId (strip /XXX suffix)
  const cleanLocalId = localId.split('/')[0].trim();

  // Construct the card ID: {setId}-{localId}
  const cardId = `${setId}-${cleanLocalId}`;
  const cardIdLower = cardId.toLowerCase();

  // Direct O(1) lookup
  let card = cardsById.get(cardId) || cardsById.get(cardIdLower);

  // Try with normalized number (handles leading zeros)
  if (!card) {
    const normalizedLocalId = normalizeCardNumber(cleanLocalId);
    const normalizedCardId = `${setId}-${normalizedLocalId}`;
    card = cardsById.get(normalizedCardId) || cardsById.get(normalizedCardId.toLowerCase());
  }

  const elapsed = Date.now() - startTime;

  if (card) {
    // VALIDATE: Check if card name matches expected name (fuzzy match)
    // This catches AI errors like saying "Houndoom" but set_id points to "Snubbull"
    if (expectedName) {
      const expectedLower = expectedName.toLowerCase()
        .replace(/[★δ]/g, '')  // Remove special chars
        .replace(/\s*(prime|lv\.x|ex|gx|v|vmax|vstar|break)\s*/gi, '')  // Remove suffixes
        .trim();
      const cardNameLower = card.name.toLowerCase()
        .replace(/[★δ]/g, '')
        .replace(/\s*(prime|lv\.x|ex|gx|v|vmax|vstar|break)\s*/gi, '')
        .trim();

      // Check if names match (either contains the other)
      const nameMatches = cardNameLower.includes(expectedLower) ||
                          expectedLower.includes(cardNameLower) ||
                          cardNameLower === expectedLower;

      if (!nameMatches) {
        console.log(`[CardLookup] DIRECT ID REJECTED: ${card.id} (expected "${expectedName}" but got "${card.name}")`);
        return null;  // Reject - AI got the set wrong!
      }
    }

    console.log(`[CardLookup] DIRECT ID HIT: ${card.id} in ${elapsed}ms`);
    return { ...card, source: 'direct' };
  }

  console.log(`[CardLookup] DIRECT ID MISS: ${cardId} in ${elapsed}ms`);
  return null;
}

/**
 * Fetch card directly from TCGdex API by constructed ID
 * VALIDATES that the card name matches to catch AI set_id errors
 *
 * @param {string} setId - Set ID from Gemini
 * @param {string} localId - Card number
 * @param {string} expectedName - Pokemon name from Gemini for validation
 * @param {string} language - Language code (en, ja, fr, de, it, es, pt)
 * @returns {object|null} - Card data or null
 */
async function fetchTCGdexDirectId(setId, localId, expectedName = '', language = DEFAULT_LANGUAGE) {
  if (!setId || !localId) return null;

  const startTime = Date.now();
  const cleanLocalId = localId.split('/')[0].trim();
  const cardId = `${setId}-${cleanLocalId}`;
  const lang = SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;

  // Helper to validate name match
  const validateName = (cardName) => {
    if (!expectedName) return true;
    const expectedLower = expectedName.toLowerCase()
      .replace(/[★δ]/g, '')
      .replace(/\s*(prime|lv\.x|ex|gx|v|vmax|vstar|break)\s*/gi, '')
      .trim();
    const cardNameLower = cardName.toLowerCase()
      .replace(/[★δ]/g, '')
      .replace(/\s*(prime|lv\.x|ex|gx|v|vmax|vstar|break)\s*/gi, '')
      .trim();
    return cardNameLower.includes(expectedLower) ||
           expectedLower.includes(cardNameLower) ||
           cardNameLower === expectedLower;
  };

  try {
    console.log(`[CardLookup] TCGdex direct fetch (${lang}): ${cardId}`);

    const res = await fetch(`https://api.tcgdex.net/v2/${lang}/cards/${cardId}`);

    if (res.ok) {
      const data = await res.json();
      const elapsed = Date.now() - startTime;

      // Validate name matches
      if (!validateName(data.name)) {
        console.log(`[CardLookup] TCGDEX DIRECT REJECTED: ${cardId} (expected "${expectedName}" but got "${data.name}")`);
        return null;
      }

      console.log(`[CardLookup] TCGDEX DIRECT HIT: ${cardId} in ${elapsed}ms`);

      return {
        id: data.id,
        name: data.name,
        localId: data.localId,
        setId: data.set?.id,
        setName: data.set?.name,
        image: data.image,
        source: 'tcgdex-direct',
      };
    }

    // Try with normalized number
    const normalizedLocalId = normalizeCardNumber(cleanLocalId);
    if (normalizedLocalId !== cleanLocalId) {
      const normalizedCardId = `${setId}-${normalizedLocalId}`;
      console.log(`[CardLookup] TCGdex retry with normalized (${lang}): ${normalizedCardId}`);

      const res2 = await fetch(`https://api.tcgdex.net/v2/${lang}/cards/${normalizedCardId}`);
      if (res2.ok) {
        const data = await res2.json();

        // Validate name matches
        if (!validateName(data.name)) {
          console.log(`[CardLookup] TCGDEX DIRECT REJECTED: ${normalizedCardId} (expected "${expectedName}" but got "${data.name}")`);
          return null;
        }

        const elapsed = Date.now() - startTime;
        console.log(`[CardLookup] TCGDEX DIRECT HIT (normalized): ${normalizedCardId} in ${elapsed}ms`);

        return {
          id: data.id,
          name: data.name,
          localId: data.localId,
          setId: data.set?.id,
          setName: data.set?.name,
          image: data.image,
          source: 'tcgdex-direct',
        };
      }
    }

    const elapsed = Date.now() - startTime;
    console.log(`[CardLookup] TCGDEX DIRECT MISS: ${cardId} in ${elapsed}ms`);
    return null;

  } catch (e) {
    console.log(`[CardLookup] TCGDEX DIRECT ERROR: ${e.message}`);
    return null;
  }
}

/**
 * Search local database by name and card number (FALLBACK)
 * @param {string} name - Card name to search for
 * @param {string} localId - Card number (e.g., "SWSH050" or "19")
 * @param {string} setId - Optional set ID to filter by
 * @param {string} setName - Optional set name to filter by
 * @returns {object|null} - Card data or null if not found
 */
function searchLocalByName(name, localId, setId = '', setName = '') {
  initializeLocalDB();

  const startTime = Date.now();
  const nameLower = name.toLowerCase();
  const targetId = localId.toLowerCase();
  const targetSetId = setId?.toLowerCase() || '';
  const targetSetName = setName?.toLowerCase() || '';

  // Get all cards with this name
  const candidates = cardsByName.get(nameLower) || [];

  // Also try partial name match (e.g., "Charizard V" might be stored as "Charizard V")
  let allCandidates = [...candidates];
  if (candidates.length === 0) {
    // Try to find cards that start with or contain this name
    for (const [key, cards] of cardsByName) {
      if (key.includes(nameLower) || nameLower.includes(key)) {
        allCandidates.push(...cards);
      }
    }
  }

  // If we have set info, filter candidates by set first
  if (targetSetId || targetSetName) {
    const setFiltered = allCandidates.filter(c => {
      const cardSetId = c.setId?.toLowerCase() || '';
      const cardSetName = c.setName?.toLowerCase() || '';
      // Check if set matches (partial match for flexibility)
      if (targetSetId && cardSetId.includes(targetSetId)) return true;
      if (targetSetId && targetSetId.includes(cardSetId)) return true;
      if (targetSetName && cardSetName.includes(targetSetName)) return true;
      if (targetSetName && targetSetName.includes(cardSetName)) return true;
      return false;
    });
    // Only use filtered list if we found matches, otherwise skip set filtering
    if (setFiltered.length > 0) {
      allCandidates = setFiltered;
      console.log(`[CardLookup] Set filter: ${allCandidates.length} candidates match set "${targetSetId || targetSetName}"`);
    } else {
      console.log(`[CardLookup] Set filter: No matches for set "${targetSetId || targetSetName}", skipping set filter`);
      // Return null if set was specified but no matches - don't return wrong card
      if (targetSetId || targetSetName) {
        console.log(`[CardLookup] LOCAL NAME MISS: Set mismatch for ${name} #${localId} in set ${targetSetId || targetSetName}`);
        return null;
      }
    }
  }

  // Normalize the target for fuzzy matching
  const normalizedTarget = normalizeCardNumber(targetId);
  let matchType = null;

  // Step 1: Exact string match
  let match = allCandidates.find(c => c.localId?.toLowerCase() === targetId);
  if (match) matchType = 'exact';

  // Step 2: Normalized match (handles "020" vs "20", "SWSH050" vs "SWSH50")
  if (!match) {
    match = allCandidates.find(c => {
      const normalizedCard = normalizeCardNumber(c.localId);
      return normalizedCard === normalizedTarget;
    });
    if (match) matchType = 'normalized';
  }

  const elapsed = Date.now() - startTime;
  if (match) {
    const matchInfo = matchType === 'normalized'
      ? `(fuzzy: "${targetId}" → "${normalizedTarget}" matched "${match.localId}")`
      : '(exact)';
    console.log(`[CardLookup] LOCAL NAME HIT ${matchInfo}: ${match.id} in ${elapsed}ms`);
    return { ...match, source: 'local-name' };
  } else {
    console.log(`[CardLookup] LOCAL NAME MISS: ${name} #${localId} in ${elapsed}ms (searched ${allCandidates.length} candidates)`);
  }

  return null;
}

/**
 * Search Supabase for newer cards not in local DB
 * @param {string} setId - Set ID from Gemini
 * @param {string} localId - Card number
 * @returns {object|null} - Card data or null
 */
async function searchSupabase(setId, localId) {
  if (!setId || !localId) return null;

  const startTime = Date.now();
  const cleanLocalId = localId.split('/')[0].trim();

  try {
    console.log(`[CardLookup] Checking Supabase for: ${setId}-${cleanLocalId}`);

    const { data, error } = await supabase
      .from('pokemon_cards')
      .select('*')
      .eq('set_id', setId)
      .eq('local_id', cleanLocalId)
      .limit(1)
      .single();

    const elapsed = Date.now() - startTime;

    if (error) {
      console.log(`[CardLookup] SUPABASE MISS: ${elapsed}ms (${error.message})`);
      return null;
    }

    if (data) {
      console.log(`[CardLookup] SUPABASE HIT: ${data.id} in ${elapsed}ms`);
      return {
        id: data.id,
        name: data.name,
        localId: data.local_id,
        setId: data.set_id,
        setName: data.set_name,
        image: data.image_url,
        source: 'supabase',
      };
    }

    console.log(`[CardLookup] SUPABASE MISS: ${elapsed}ms`);
    return null;
  } catch (e) {
    console.log(`[CardLookup] SUPABASE ERROR: ${e.message}`);
    return null;
  }
}

/**
 * Fallback TCGdex API search by name (last resort)
 * @param {string} name - Card name
 * @param {string} localId - Card number
 * @param {string} language - Language code (en, ja, fr, de, it, es, pt)
 * @returns {object|null} - Card data or null
 */
async function searchTCGdexByName(name, localId, language = DEFAULT_LANGUAGE) {
  const startTime = Date.now();
  const lang = SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;

  try {
    console.log(`[CardLookup] TCGdex name search (${lang}): ${name} #${localId}`);

    // Search by name
    const searchRes = await fetch(`https://api.tcgdex.net/v2/${lang}/cards?name=${encodeURIComponent(name)}`);
    if (!searchRes.ok) {
      console.log(`[CardLookup] TCGDEX SEARCH FAILED: ${searchRes.status}`);
      return null;
    }

    const results = await searchRes.json();
    console.log(`[CardLookup] TCGdex returned ${results.length} results, filtering by #${localId}`);

    // Find match by card number
    const targetId = localId.toLowerCase();
    let match = results.find(r => r.localId?.toString().toLowerCase() === targetId);

    if (!match) {
      const numericTarget = targetId.replace(/\D/g, '');
      match = results.find(r => r.localId?.toString().replace(/\D/g, '') === numericTarget);
    }

    // NO "best effort" fallback - if number doesn't match, return null
    // This allows Strategy 7 (community cards) to run for cards not in TCGdex

    if (match) {
      // Fetch full details
      const detailRes = await fetch(`https://api.tcgdex.net/v2/${lang}/cards/${match.id}`);
      if (detailRes.ok) {
        const details = await detailRes.json();
        const elapsed = Date.now() - startTime;
        console.log(`[CardLookup] TCGDEX NAME HIT: ${match.id} in ${elapsed}ms`);

        return {
          id: match.id,
          name: details.name,
          localId: details.localId,
          setId: details.set?.id,
          setName: details.set?.name,
          image: details.image,
          source: 'tcgdex-name',
        };
      }
    }

    const elapsed = Date.now() - startTime;
    console.log(`[CardLookup] TCGDEX NAME MISS: ${elapsed}ms`);
    return null;
  } catch (e) {
    console.log(`[CardLookup] TCGDEX NAME ERROR: ${e.message}`);
    return null;
  }
}

/**
 * STRATEGY 7: Search community-submitted cards (ai_card_submissions)
 * These are cards added via Chase's Research Lab that aren't in TCGdex
 */
async function searchCommunityCards(name, localId, language = 'en') {
  const start = Date.now();

  try {
    console.log(`[CardLookup] Community cards search: ${name} #${localId}`);

    // Search by name and number
    const { data, error } = await supabase
      .from('ai_card_submissions')
      .select('*')
      .or(`name.ilike.%${name}%,name_en.ilike.%${name}%`)
      .limit(10);

    if (error) {
      console.log(`[CardLookup] COMMUNITY ERROR: ${error.message}`);
      return null;
    }

    if (!data || data.length === 0) {
      console.log(`[CardLookup] COMMUNITY MISS: ${Date.now() - start}ms`);
      return null;
    }

    // Find exact match by number if provided
    let match = null;
    if (localId) {
      match = data.find(card => {
        const cardNum = (card.number || '').split('/')[0].trim();
        return cardNum === localId;
      });
    }

    // If no exact number match, take first result
    if (!match) {
      match = data[0];
    }

    // Check both image_url and digital_image_url (schema evolved)
    const imageUrl = match.digital_image_url || match.image_url || null;
    console.log(`[CardLookup] COMMUNITY HIT: ${match.name || match.name_en} in ${Date.now() - start}ms`);
    console.log(`[CardLookup] COMMUNITY IMAGE: ${imageUrl || 'NULL'}`);

    // Increment use count (fire and forget) - wrapped to prevent crashes
    try {
      supabase.rpc('increment_submission_use', { submission_id: match.id });
    } catch (rpcErr) {
      // Ignore - just tracking usage
    }

    return {
      id: `community-${match.id}`,
      name: match.name || match.name_en,
      localId: match.number,
      setId: match.set_id,
      setName: match.set_name,
      image: imageUrl,
      source: 'community',
      // Include all extra data for display
      rarity: match.rarity,
      hp: match.hp,
      types: match.types,
      illustrator: match.illustrator,
      language: match.language,
    };
  } catch (e) {
    console.log(`[CardLookup] COMMUNITY ERROR: ${e.message}`);
    return null;
  }
}

/**
 * Main lookup function - NEW STRATEGY WITH MULTI-LANGUAGE SUPPORT
 *
 * Priority:
 *   1. Direct ID: {setId}-{localId} from local DB (0ms) - English only
 *   2. Direct ID: {setId}-{localId} from TCGdex (~200ms) - Language-specific
 *   3. Supabase by setId + localId (~100ms)
 *   4. Local DB by name + number (~5ms) - English only
 *   5. TCGdex by name search (~400ms) - Language-specific
 *   6. FALLBACK: If non-English fails, retry with English + englishName
 *
 * @param {string} name - Card name from AI (in detected language)
 * @param {string} cardNumber - Card number from AI (e.g., "SWSH050" or "19/189")
 * @param {string} setName - Set name hint from AI (optional, for logging)
 * @param {string} setId - Set ID from AI (e.g., "ex15", "sm9") - THE KEY!
 * @param {string} language - Language code (en, ja, fr, de, it, es, pt)
 * @param {string} englishName - English name for non-English cards (fallback)
 * @returns {object|null} - Card data with image URLs
 */
export async function findCard(name, cardNumber, setName = '', setId = '', language = 'en', englishName = '') {
  const totalStart = Date.now();
  const localId = cardNumber.split('/')[0].trim();
  const lang = SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;
  const isEnglish = lang === 'en';

  console.log(`[CardLookup] ======== Finding: ${name} #${localId} (set: ${setId || 'unknown'}, lang: ${lang}) ========`);

  let card = null;

  // STRATEGY 1: Direct ID lookup from local DB (fastest, most accurate)
  // Local DB is English only, so use englishName for non-English cards
  // Pass name for validation - catches AI set_id errors
  if (setId) {
    const lookupName = isEnglish ? name : (englishName || name);
    card = lookupDirectId(setId, localId, lookupName);
  }

  // STRATEGY 2: Direct ID from TCGdex API (with name validation)
  // Use language-specific endpoint
  if (!card && setId) {
    card = await fetchTCGdexDirectId(setId, localId, name, lang);
  }

  // STRATEGY 3: Supabase by setId + localId
  if (!card && setId) {
    card = await searchSupabase(setId, localId);
  }

  // STRATEGY 4: Local DB by name (fallback for when setId is wrong/missing)
  // Local DB is English only, so use englishName for non-English cards
  // Pass setId and setName to avoid matching wrong cards
  if (!card) {
    const lookupName = isEnglish ? name : (englishName || name);
    card = searchLocalByName(lookupName, localId, setId, setName);
  }

  // STRATEGY 5: TCGdex name search - Use detected language
  if (!card) {
    card = await searchTCGdexByName(name, localId, lang);
  }

  // STRATEGY 6: FALLBACK - If non-English failed, try English with englishName
  // Then re-fetch the localized version using the found card ID
  if (!card && !isEnglish && englishName) {
    console.log(`[CardLookup] Non-English lookup failed, trying English fallback: ${englishName}`);

    let englishCard = null;

    // Try TCGdex direct with English
    if (setId) {
      englishCard = await fetchTCGdexDirectId(setId, localId, englishName, 'en');
    }

    // Try TCGdex name search with English
    if (!englishCard) {
      englishCard = await searchTCGdexByName(englishName, localId, 'en');
    }

    // If found in English, re-fetch the localized (e.g., Japanese) version
    if (englishCard) {
      console.log(`[CardLookup] Found via English, fetching ${lang} version: ${englishCard.id}`);

      try {
        const langRes = await fetch(`https://api.tcgdex.net/v2/${lang}/cards/${englishCard.id}`);
        if (langRes.ok) {
          const langData = await langRes.json();
          card = {
            id: langData.id,
            name: langData.name,
            localId: langData.localId,
            setId: langData.set?.id,
            setName: langData.set?.name,
            image: langData.image,
            source: 'tcgdex-fallback-localized',
          };
          console.log(`[CardLookup] Got ${lang} version: ${langData.name}`);
        } else {
          // Localized version not available, use English
          card = { ...englishCard, actualLanguage: 'en' };
          console.log(`[CardLookup] ${lang} version not available, using English`);
        }
      } catch (e) {
        // Error fetching localized, use English
        card = { ...englishCard, actualLanguage: 'en' };
        console.log(`[CardLookup] Error fetching ${lang} version, using English: ${e.message}`);
      }
    }
  }

  // STRATEGY 7: Community-submitted cards (Chase's Research Lab)
  // Last resort - check if another user already added this card
  if (!card) {
    card = await searchCommunityCards(name, localId, lang);
  }

  const totalElapsed = Date.now() - totalStart;

  if (card) {
    console.log(`[CardLookup] ======== FOUND: ${card.id} via ${card.source} in ${totalElapsed}ms ========`);

    // Community cards have direct image URLs (not TCGdex format)
    if (card.source === 'community') {
      return {
        id: card.id,
        name: card.name,
        localId: card.localId,
        setId: card.setId,
        setName: card.setName,
        image: card.image,  // Direct URL
        imageHigh: card.image,  // Same for community cards
        tcgId: card.id,
        lookupTime: totalElapsed,
        source: card.source,
        language: card.language || lang,
        // Extra community data
        rarity: card.rarity,
        hp: card.hp,
        types: card.types,
        illustrator: card.illustrator,
      };
    }

    return {
      id: card.id,
      name: card.name,
      localId: card.localId,
      setId: card.setId,
      setName: card.setName,
      image: card.image ? `${card.image}/low.webp` : null,
      imageHigh: card.image ? `${card.image}/high.webp` : null,
      tcgId: card.id,
      lookupTime: totalElapsed,
      source: card.source,
      language: card.actualLanguage || lang,  // Use actual language if fallback occurred
    };
  }

  console.log(`[CardLookup] ======== NOT FOUND: ${name} #${localId} (${totalElapsed}ms) ========`);
  return null;
}

/**
 * Get database stats
 */
export function getLocalDBStats() {
  initializeLocalDB();
  return {
    totalCards: localCardsData.length,
    uniqueNames: cardsByName.size,
  };
}

/**
 * Search local database by card number only (across all sets)
 * Returns array of matches with TCGdex-compatible format
 */
export function searchLocalByNumber(searchNumber) {
  initializeLocalDB();
  const normalize = (n) => {
    if (!n) return '';
    const clean = n.toString().trim().toUpperCase();
    if (/^\d+$/.test(clean)) return parseInt(clean, 10).toString();
    return clean.replace(/([A-Z]+)0+([1-9]\d*)/i, '$1$2');
  };
  const target = normalize(searchNumber);
  if (!target) return [];

  const results = [];
  for (const card of localCardsData) {
    const cardNum = normalize(card.localId || card.id?.split('-').pop() || '');
    if (cardNum === target) {
      results.push({
        name: card.name,
        setName: card.set?.name || card.setId || 'Unknown Set',
        setCode: card.set?.id || card.setId || '',
        number: card.localId || '',
        image: card.image ? `${card.image}/low.webp` : null,
        imageHigh: card.image ? `${card.image}/high.webp` : null,
        tcgId: card.id,
      });
    }
  }
  return results;
}

export default { findCard, getLocalDBStats, searchLocalByNumber };
